/**
 * BrandingPanel — collapsible school/teacher branding for Lesson Plans and Activity Sheets.
 *
 * Visual-only: branding fields never affect instructional content or the LessonBlueprint.
 * Logo is stored as a base64 data URL.  See branding.ts for migration notes.
 */

import React, { useRef } from 'react';
import { Building2, ChevronDown, ChevronUp, Upload, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { type BrandingData } from './branding';

interface BrandingPanelProps {
  branding: BrandingData;
  onChange: (b: BrandingData) => void;
  open: boolean;
  onToggle: () => void;
}

const FIELD_LABELS: { key: keyof BrandingData['visibleFields']; label: string; dataKey: keyof BrandingData }[] = [
  { key: 'schoolName',   label: 'School name',    dataKey: 'schoolName'   },
  { key: 'districtName', label: 'District name',  dataKey: 'districtName' },
  { key: 'teacherName',  label: 'Teacher name',   dataKey: 'teacherName'  },
  { key: 'courseClass',  label: 'Course / class', dataKey: 'courseClass'  },
  { key: 'period',       label: 'Period',         dataKey: 'period'       },
  { key: 'schoolYear',   label: 'School year',    dataKey: 'schoolYear'   },
];

export function BrandingPanel({ branding, onChange, open, onToggle }: BrandingPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);

  function set<K extends keyof BrandingData>(key: K, value: BrandingData[K]) {
    onChange({ ...branding, [key]: value });
  }

  function setVisible(key: keyof BrandingData['visibleFields'], value: boolean) {
    onChange({ ...branding, visibleFields: { ...branding.visibleFields, [key]: value } });
  }

  function handleLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) {
      alert('Logo file is too large (max 500 KB). Please use a smaller image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      const url = ev.target?.result as string;
      onChange({ ...branding, logoDataUrl: url, visibleFields: { ...branding.visibleFields, logo: true } });
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden print:hidden">
      {/* Header trigger */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2.5 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left"
      >
        <Building2 size={15} className="text-slate-400 flex-shrink-0" />
        <span className="text-sm font-semibold text-slate-700 flex-1">School Branding</span>
        <span className="text-xs text-slate-400">Optional header info</span>
        {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 border-t border-slate-50 space-y-4">
              {/* Text fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FIELD_LABELS.map(({ key, label, dataKey }) => (
                  <div key={key} className="flex items-start gap-2">
                    {/* Visibility checkbox */}
                    <input
                      type="checkbox"
                      id={`brand-vis-${key}`}
                      checked={branding.visibleFields[key]}
                      onChange={e => setVisible(key, e.target.checked)}
                      className="mt-2.5 w-3.5 h-3.5 rounded border-slate-300 text-primary focus:ring-primary flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <label htmlFor={`brand-field-${key}`} className="text-xs font-medium text-slate-500 block mb-1">
                        {label}
                      </label>
                      <input
                        id={`brand-field-${key}`}
                        type="text"
                        value={branding[dataKey] as string}
                        onChange={e => set(dataKey, e.target.value)}
                        placeholder={label}
                        disabled={!branding.visibleFields[key]}
                        className="w-full h-8 px-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-primary/40 disabled:opacity-40 disabled:bg-slate-50"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Logo upload */}
              <div className="flex items-start gap-2 pt-1 border-t border-slate-50">
                <input
                  type="checkbox"
                  id="brand-vis-logo"
                  checked={branding.visibleFields.logo}
                  onChange={e => setVisible('logo', e.target.checked)}
                  className="mt-2 w-3.5 h-3.5 rounded border-slate-300 text-primary focus:ring-primary flex-shrink-0"
                />
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 block mb-1">School logo</label>
                    <p className="text-[10px] text-slate-400">PNG or JPG, max 500 KB. Stored locally in your browser.</p>
                  </div>
                  {branding.logoDataUrl ? (
                    <div className="flex items-center gap-2 ml-auto">
                      <img
                        src={branding.logoDataUrl}
                        alt="School logo"
                        className="h-10 w-auto rounded border border-slate-100 object-contain"
                      />
                      <button
                        onClick={() => onChange({ ...branding, logoDataUrl: '', visibleFields: { ...branding.visibleFields, logo: false } })}
                        className="p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        title="Remove logo"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => fileRef.current?.click()}
                        disabled={!branding.visibleFields.logo}
                        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"
                      >
                        <Upload size={12} /> Upload
                      </button>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
                    </>
                  )}
                </div>
              </div>

              <p className="text-[10px] text-slate-400 pt-1">
                Branding is cosmetic only — it never changes instructional content.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
